import { useState } from "react"
import { ActivityIndicator, Pressable, View } from "react-native"
import { useLocalSearchParams, router, Link } from "expo-router"

import { ar } from "@src/i18n/ar"
import { trpc, formatPrice, sharedStyles, dayjs, localDateToUnix, unixToLocalDate } from "@src/utils"
import { Header, Text, DatePicker, List, ModalWrapper, MCIcon } from "@src/components"
import { Button } from "@src/components/ui/button"
import { SafeAreaView } from "react-native-safe-area-context"

export default function SalesDetails() {
  const params = useLocalSearchParams<{ shiftId?: string; from?: string; to?: string }>()
  const [filterVisible, setFilterVisible] = useState(false)

  const shiftId = params.shiftId ? Number(params.shiftId) : undefined
  const fromUnix = params.from ? Number(params.from) : dayjs().subtract(7, "day").startOf("day").unix()
  const toUnix = params.to ? Number(params.to) : dayjs().endOf("day").unix()

  const [localFrom, setLocalFrom] = useState(fromUnix)
  const [localTo, setLocalTo] = useState(toUnix)

  const input = shiftId ? { shiftId, limit: 20 } : { from: fromUnix, to: toUnix, limit: 20 }

  const sales = trpc.sales.details.useInfiniteQuery(input, {
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })

  const firstPage = sales.data?.pages[0]
  const allProducts = sales.data?.pages.flatMap((page) => page.products) ?? []
  const shift = firstPage?.shift
  const totals = firstPage?.totals

  const applyFilters = () => {
    router.setParams({ from: localFrom.toString(), to: localTo.toString() })
    setFilterVisible(false)
  }

  const emptyView = () => {
    if (sales.isError) return <Text className="text-red-500 text-center py-8">{ar.somethingWentWrong}</Text>
    if (sales.isLoading) return <ActivityIndicator className="py-4" />
    if (allProducts.length === 0) return <Text className="text-gray-500 text-center py-8">{ar.noData}</Text>
    return null
  }

  return (
    <>
      <Header
        title={ar.salesDetails}
        right={
          !shiftId ? (
            <Pressable
              onPress={() => {
                setLocalFrom(fromUnix)
                setLocalTo(toUnix)
                setFilterVisible(true)
              }}
              className="px-2 py-1 active:opacity-70 h-full justify-center items-center"
            >
              <MCIcon name="filter-outline" size={26} color="#374151" />
            </Pressable>
          ) : undefined
        }
      />

      {filterVisible && (
        <ModalWrapper onClose={() => setFilterVisible(false)}>
          <View className="gap-4">
            <Text className="font-bold text-lg text-center mb-2">{ar.applyFilters}</Text>
            <View>
              <Text className="mb-2 font-medium">{ar.dateRange}</Text>
              <View className="flex-row gap-2">
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.fromDate}</Text>
                  <DatePicker mode="datetime" value={unixToLocalDate(localFrom)} onChange={(d) => d && setLocalFrom(localDateToUnix(d))} placeholder={ar.fromDate} />
                </View>
                <View className="flex-1">
                  <Text className="mb-1 text-xs">{ar.toDate}</Text>
                  <DatePicker mode="datetime" value={unixToLocalDate(localTo)} onChange={(d) => d && setLocalTo(localDateToUnix(d))} placeholder={ar.toDate} />
                </View>
              </View>
            </View>
            <Button onPress={applyFilters}>{ar.apply}</Button>
          </View>
        </ModalWrapper>
      )}

      <List
        data={allProducts}
        keyExtractor={(item) => String(item.productId)}
        contentContainerStyle={sharedStyles.contentContainer}
        onRefresh={() => sales.refetch()}
        ListHeaderComponent={
          <>
            {shift && (
              <View className="bg-white p-4 mx-2 mt-2 rounded-xl border border-gray-100">
                <View className="flex-row justify-between mb-2">
                  <Text className="font-bold text-gray-800">{shift.employeeName ?? ar.employee}</Text>
                  <Text className="text-gray-500 text-sm">{shift.startTime ? unixToLocalDate(shift.startTime).toLocaleDateString() : ""}</Text>
                </View>
                <View className="flex-row justify-between">
                  <Text className="text-gray-600">
                    {ar.cash}: {formatPrice(shift.cash)}
                  </Text>
                  <Text className="text-gray-600">
                    {ar.visa}: {formatPrice(shift.visa)}
                  </Text>
                  <Text className="text-gray-600">
                    {ar.wallet}: {formatPrice(shift.wallet)}
                  </Text>
                </View>
              </View>
            )}
            {totals && (
              <View className="flex-row bg-white p-3 mx-2 mt-2 rounded-xl border border-gray-100">
                <View className="flex-1 items-center">
                  <Text className="text-xs text-gray-500">{ar.totalRevenue}</Text>
                  <Text className="font-bold text-green-600">
                    {formatPrice(totals.totalEGP)} {ar.egp}
                  </Text>
                </View>
                <View className="flex-1 items-center">
                  <Text className="text-xs text-gray-500">{ar.totalSold}</Text>
                  <Text className="font-bold text-gray-800">{totals.totalItems}</Text>
                </View>
                <View className="flex-1 items-center">
                  <Text className="text-xs text-gray-500">{ar.totalReceipts}</Text>
                  <Text className="font-bold text-gray-800">{totals.totalReceipts}</Text>
                </View>
              </View>
            )}
          </>
        }
        renderItem={({ item }) => {
          const isWeight = item.type === "WEIGHT_PRODUCT" || item.type === "WEIGHT_DEFINED_PRODUCT"
          const qtyDisplay = isWeight ? `${(item.totalWeightGrams / 1000).toFixed(2)} ${ar.kg}` : `${item.totalQuantity} ${ar.piece}`

          return (
            <Link asChild href={`/dashboard/products/${item.productId}`}>
              <Pressable className="bg-white p-4 mx-2 mb-2 rounded-xl border border-gray-100">
                <View className="flex-row justify-between items-center">
                  <Text className="font-bold text-gray-800 flex-1" numberOfLines={1}>
                    {item.name}
                  </Text>
                  <Text className="font-bold text-green-600">
                    {formatPrice(item.totalEGP)} {ar.egp}
                  </Text>
                </View>
                <View className="flex-row justify-between items-center mt-1">
                  <Text className="text-xs text-gray-400">{item.upc}</Text>
                  <Text className="text-sm text-gray-600">{qtyDisplay}</Text>
                </View>
              </Pressable>
            </Link>
          )
        }}
        ListEmptyComponent={emptyView}
        ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        onEndReachedThreshold={0.8}
        onEndReached={() => {
          if (sales.hasNextPage) sales.fetchNextPage()
        }}
      />
    </>
  )
}
